import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, User, MapPin, Ruler, Heart } from "@phosphor-icons/react";
import api from "../lib/api";
import CustomerPhotoField from "../components/CustomerPhotoField";

const FIELDS = [
  "full_name", "phone_mobile", "phone_landline", "whatsapp",
  "cep", "street", "number", "complement", "neighborhood", "city", "state",
  "shirt_size", "pants_size", "dress_size", "shoe_size",
  "bust", "waist", "hip", "shoulder", "arm_circumference", "height", "weight",
  "preferred_size", "preferred_style", "favorite_colors", "preferred_brand",
  "note",
];

const emptyForm = () => FIELDS.reduce((acc, k) => ({ ...acc, [k]: "" }), {});

export default function MyProfile() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [handle, setHandle] = useState("");
  const [email, setEmail] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/customer");
      const next = { ...emptyForm() };
      FIELDS.forEach(k => { next[k] = data[k] != null ? String(data[k]) : ""; });
      setForm(next);
      setHandle(data.handle || "");
      setEmail(data.email || "");
      setPhotoUrl(data.photo_url || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível carregar seu cadastro");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const lookupCep = async (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setLoadingCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const j = await resp.json();
      if (!j.erro) {
        setForm(f => ({
          ...f,
          street: j.logradouro || f.street,
          neighborhood: j.bairro || f.neighborhood,
          city: j.localidade || f.city,
          state: j.uf || f.state,
        }));
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("[MyProfile] ViaCEP failed", err);
    } finally { setLoadingCep(false); }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => String(v).trim() !== ""),
      );
      payload.photo_url = photoUrl || "";
      await api.put("/me/customer", payload);
      toast.success("Cadastro atualizado com sucesso");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div data-testid="my-profile-loading" className="text-brand-ink2">Carregando seu cadastro…</div>;
  }

  return (
    <div data-testid="my-profile-page" className="space-y-6 max-w-4xl">
      <header>
        <p className="label-brutal">Meu cadastro</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Seus dados</h1>
        <p className="text-brand-ink2 mt-2">
          Vinculado a <strong>{handle}</strong>
          {email && <> · <span className="text-brand-ink2">{email}</span></>}
        </p>
      </header>

      <Section title="Contato" icon={User}>
        <div className="mb-4">
          <p className="label-brutal mb-2">Sua foto</p>
          <CustomerPhotoField
            value={photoUrl}
            onChange={(url) => setPhotoUrl(url || "")}
            testid="mp-photo"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Nome completo" testid="mp-full_name" value={form.full_name} onChange={(v) => setField("full_name", v)} />
          <Field label="Celular" testid="mp-phone_mobile" value={form.phone_mobile} onChange={(v) => setField("phone_mobile", v)} placeholder="(11) 91234-5678" />
          <Field label="WhatsApp" testid="mp-whatsapp" value={form.whatsapp} onChange={(v) => setField("whatsapp", v)} placeholder="(11) 91234-5678" />
          <Field label="Telefone fixo" testid="mp-phone_landline" value={form.phone_landline} onChange={(v) => setField("phone_landline", v)} />
        </div>
      </Section>

      <Section title="Endereço para envio" icon={MapPin}>
        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_120px] gap-3">
          <Field
            label="CEP" testid="mp-cep" value={form.cep}
            onChange={(v) => { setField("cep", v); if (v.replace(/\D/g, "").length === 8) lookupCep(v); }}
            placeholder="00000-000" hint={loadingCep ? "Buscando…" : undefined}
          />
          <Field label="Rua" testid="mp-street" value={form.street} onChange={(v) => setField("street", v)} />
          <Field label="Número" testid="mp-number" value={form.number} onChange={(v) => setField("number", v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_80px] gap-3 mt-3">
          <Field label="Complemento" testid="mp-complement" value={form.complement} onChange={(v) => setField("complement", v)} />
          <Field label="Bairro" testid="mp-neighborhood" value={form.neighborhood} onChange={(v) => setField("neighborhood", v)} />
          <Field label="Cidade" testid="mp-city" value={form.city} onChange={(v) => setField("city", v)} />
          <Field label="UF" testid="mp-state" value={form.state} onChange={(v) => setField("state", v.toUpperCase().slice(0, 2))} />
        </div>
      </Section>

      <Section title="Suas medidas" icon={Ruler} hint="Ajuda a loja a sugerir peças que servem em você.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Blusa (P/M/G)" testid="mp-shirt_size" value={form.shirt_size} onChange={(v) => setField("shirt_size", v)} />
          <Field label="Calça (36-52)" testid="mp-pants_size" value={form.pants_size} onChange={(v) => setField("pants_size", v)} />
          <Field label="Vestido" testid="mp-dress_size" value={form.dress_size} onChange={(v) => setField("dress_size", v)} />
          <Field label="Calçado" testid="mp-shoe_size" value={form.shoe_size} onChange={(v) => setField("shoe_size", v)} />
          <Field label="Busto (cm)" testid="mp-bust" value={form.bust} onChange={(v) => setField("bust", v)} />
          <Field label="Cintura (cm)" testid="mp-waist" value={form.waist} onChange={(v) => setField("waist", v)} />
          <Field label="Quadril (cm)" testid="mp-hip" value={form.hip} onChange={(v) => setField("hip", v)} />
          <Field label="Ombro (cm)" testid="mp-shoulder" value={form.shoulder} onChange={(v) => setField("shoulder", v)} />
          <Field label="Braço (cm)" testid="mp-arm" value={form.arm_circumference} onChange={(v) => setField("arm_circumference", v)} />
          <Field label="Altura (cm)" testid="mp-height" value={form.height} onChange={(v) => setField("height", v)} />
          <Field label="Peso (kg)" testid="mp-weight" value={form.weight} onChange={(v) => setField("weight", v)} />
        </div>
      </Section>

      <Section title="Preferências" icon={Heart} hint="Opcional — nos ajuda a te chamar quando chegar algo do seu estilo.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Tamanho preferido" testid="mp-preferred_size" value={form.preferred_size} onChange={(v) => setField("preferred_size", v)} />
          <Field label="Estilo" testid="mp-preferred_style" value={form.preferred_style} onChange={(v) => setField("preferred_style", v)} placeholder="Ex: básico, elegante, praia..." />
          <Field label="Cores favoritas" testid="mp-favorite_colors" value={form.favorite_colors} onChange={(v) => setField("favorite_colors", v)} placeholder="Ex: preto, off-white, terracota" />
          <Field label="Marca preferida" testid="mp-preferred_brand" value={form.preferred_brand} onChange={(v) => setField("preferred_brand", v)} />
        </div>
        <div className="mt-3">
          <label className="label-brutal">Observações para a loja</label>
          <textarea
            data-testid="mp-note"
            rows={3}
            className="input-brutal !h-auto"
            value={form.note}
            onChange={(e) => setField("note", e.target.value)}
            placeholder="Qualquer coisa que a loja precise saber sobre você"
          />
        </div>
      </Section>

      <div className="sticky bottom-4 flex justify-end">
        <button
          data-testid="mp-save-btn"
          onClick={save}
          disabled={saving}
          className="btn-pink h-[56px] px-6 disabled:opacity-60"
        >
          <FloppyDisk weight="bold" size={18} /> {saving ? "Salvando…" : "Salvar alterações"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, hint, children }) {
  return (
    <section className="card-brutal p-5 space-y-3">
      <div>
        <h2 className="font-heading text-xl font-black flex items-center gap-2">
          <Icon weight="bold" size={20} /> {title}
        </h2>
        {hint && <p className="text-brand-ink2 text-sm mt-1">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, testid, value, onChange, placeholder, hint }) {
  return (
    <div>
      <label className="label-brutal">{label}</label>
      <input
        data-testid={testid}
        className="input-brutal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <p className="text-[11px] text-brand-ink2 mt-1">{hint}</p>}
    </div>
  );
}
